import { useEffect, useState } from "react";
import { Banknote, Save, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "account_name", label: "Account Name", placeholder: "e.g., D Campbell t/a DC Tiling", testid: "bank-account-name" },
  { key: "sort_code", label: "Sort Code", placeholder: "00-00-00", testid: "bank-sort-code" },
  { key: "account_number", label: "Account Number", placeholder: "12345678", testid: "bank-account-number" },
  { key: "bank_name", label: "Bank Name", placeholder: "e.g., Barclays", testid: "bank-name" },
  { key: "iban", label: "IBAN (optional)", placeholder: "GB00 BANK 0000 0000 0000 00", testid: "bank-iban" },
  { key: "swift_bic", label: "SWIFT / BIC (optional)", placeholder: "BUKBGB22", testid: "bank-swift" },
  { key: "payment_reference_format", label: "Payment Reference Format", placeholder: "DCT-{quote_id}", testid: "bank-ref-format", help: "Use {quote_id} to insert the job/quote ID." },
];

const BankSettingsPanel = ({ token }) => {
  const [settings, setSettings] = useState({
    account_name: "",
    sort_code: "",
    account_number: "",
    bank_name: "",
    iban: "",
    swift_bic: "",
    payment_instructions: "",
    payment_reference_format: "DCT-{quote_id}",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/owner/bank-settings`, { headers })
      .then(r => setSettings(s => ({ ...s, ...r.data })))
      .catch(() => toast.error("Failed to load bank settings"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/bank-settings`, settings, { headers });
      toast.success("Bank settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-settings-panel">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bank Transfer Settings</h2>
          <p className="text-gray-600">Used on invoices, payment requests, and the customer portal.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-bank-settings-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Banknote className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold">Bank Account Details</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(f => (
            <div key={f.key}>
              <Label htmlFor={f.key}>{f.label}</Label>
              <Input
                id={f.key}
                placeholder={f.placeholder}
                value={settings[f.key] || ""}
                onChange={(e) => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                data-testid={f.testid}
              />
              {f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <Label htmlFor="payment_instructions">Payment Instructions (shown on invoices &amp; portal)</Label>
        <Textarea
          id="payment_instructions"
          rows={3}
          placeholder="e.g., Please pay by bank transfer using the reference shown. Funds typically clear within 1 working day."
          value={settings.payment_instructions || ""}
          onChange={(e) => setSettings(s => ({ ...s, payment_instructions: e.target.value }))}
          data-testid="bank-payment-instructions"
        />
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <strong>Reminder:</strong> Bank Transfer is now the preferred payment method. Stripe checkout has been removed sitewide.
      </div>
    </div>
  );
};

export default BankSettingsPanel;
